/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  accessAPI,
  Sidebar,
  Titulo,
  Boton,
  CampoInformacion,
  Formulario,
  TextoConFormato,
  tituloBase,
  TablaFiltrable
} from "../../shared";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Toast } from "primereact/toast";
import { Column } from "primereact/column";
import { fechaCorta } from "../../portal/utilidades";

const FRECUENCIA_LABEL = {
  SEMANAL: "Semanal",
  MENSUAL: "Mensual",
  SEMESTRAL: "Semestral",
  ANUAL: "Anual",
  BIENAL: "Bienal",
  TRIENAL: "Trienal",
};
const frecuenciaBody = (item) =>
  item.frecuenciaRefacturacion
    ? FRECUENCIA_LABEL[item.frecuenciaRefacturacion] ||
      item.frecuenciaRefacturacion
    : "Por actividad";
const precioBody = (item) =>
  `${item.precioMoneda?.simbolo || ""} ${item.precioMonto}${item.precioMoneda?.nombre ? ` (${item.precioMoneda.nombre})` : ""}`;

export default function PropuestaSabana() {
  const [propuesta, setPropuesta] = useState(null);
  // Título de la pestaña, como en el admin: el de este registro cuando carga
  useEffect(() => {
    if (propuesta) document.title = propuesta.nombre || "Propuesta sábana";
  }, [propuesta]);
  useEffect(() => () => (document.title = tituloBase()), []);
  const [loader, setLoader] = useState(true);
  const [loaderAceptacion, setLoaderAceptacion] = useState(false);
  const [datosAceptacion, setDatosAceptacion] = useState({});
  const [sidebarVisible, setSidebarVisible] = useState(false);

  const toast = useRef(null);
  const { propuestaId } = useParams();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!propuestaId) return;
    accessAPI(
      "GET",
      `cliente/propuesta/sabana/${propuestaId}`,
      null,
      (data) => {
        setPropuesta(data);
        if (data.fechaAceptacion) {
          setDatosAceptacion({
            fechaAceptacion: data.fechaAceptacion,
            nombreAceptador: data.nombreAceptador,
          });
        }
        setLoader(false);
      },
      (respuesta) => {
        setLoader(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error al cargar la propuesta. Intente nuevamente más tarde.",
          sticky: true,
        });
      }
    );
  }, [propuestaId]);

  function aceptarPropuesta(camposFormulario) {
    setLoaderAceptacion(true);
    accessAPI(
      "PUT",
      `cliente/propuesta/sabana/aceptar/${propuesta.id}`,
      camposFormulario,
      (respuesta) => {
        setDatosAceptacion({
          fechaAceptacion: respuesta.fechaAceptacion,
          nombreAceptador: respuesta.nombreAceptador,
        });
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "La propuesta sábana ha sido aceptada exitosamente.",
          life: 3000,
        });
        setLoaderAceptacion(false);
        setSidebarVisible(false);
      },
      (respuesta) => {
        setLoaderAceptacion(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail:
            respuesta[0]?.msg ||
            "Ocurrió un error al aceptar la propuesta. Intente nuevamente más tarde.",
          sticky: true,
        });
      }
    );
  }

  const formularioAceptacionConfig = {
    secciones: [
      {
        descripcion:
          "Debajo podrá aceptar la presente propuesta comercial. En caso de tener dudas o querer rechazarla, por favor póngase en contacto con nosotros.",
        campos: [
          {
            etiqueta:
              "Comprendo que esta pantalla representa una propuesta comercial. Al aceptarla se pueden disparar procesos de facturación si corresponden.",
            nombre: "aceptacion1",
            requerido: true,
            tipoitemformularioid: 6,
          },
          {
            etiqueta:
              "Leí y acepto las condiciones planteadas en los documentos de certificación.",
            nombre: "aceptacion2",
            requerido: true,
            tipoitemformularioid: 6,
          },
        ],
      },
    ],
    botones: [
      {
        etiqueta: "Aceptar propuesta",
        validar: true,
        confirmacion: "¿Confirma la decisión de aceptar la propuesta?",
        callback: aceptarPropuesta,
      },
    ],
  };

  // Agrupa los items por certificadoTipo para el listado
  function agrupar(items) {
    const grupos = [];
    const vistos = new Set();
    for (const item of items || []) {
      if (!vistos.has(item.certificadoTipoId)) {
        vistos.add(item.certificadoTipoId);
        grupos.push({
          certificadoTipoId: item.certificadoTipoId,
          certificadoTipoNombre: item.certificadoTipo?.nombre || "",
          items: (items || []).filter(
            (i) => i.certificadoTipoId === item.certificadoTipoId
          ),
        });
      }
    }
    return grupos;
  }

  return (
    <main>
      <Toast ref={toast} position="top-center" />
      {loader && (
        <>
          <Titulo urlVolver="/propuestas?tab=sabanas">
            <Skeleton width="300px" height="2rem" />
          </Titulo>
          <Card css={{ marginBottom: "1.5em" }}>
            <div
              css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
            >
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} width="100%" height="1rem" />
              ))}
            </div>
          </Card>
          <Card css={{ marginBottom: "1.5em" }}>
            <Skeleton width="60%" height="1.5rem" />
          </Card>
        </>
      )}
      {!loader && propuesta && (
        <div>
          <Titulo
            subtitulo={propuesta.fechaEmision && `Emitida el ${fechaCorta(propuesta.fechaEmision)}`}
            urlVolver="/propuestas?tab=sabanas"
            etiqueta={
              datosAceptacion?.fechaAceptacion
                ? `Aceptada el ${fechaCorta(datosAceptacion.fechaAceptacion)}`
                : "Pendiente de aceptación"
            }
            ok={!!datosAceptacion?.fechaAceptacion}
            alerta={!datosAceptacion?.fechaAceptacion}
            botones={
              !datosAceptacion?.fechaAceptacion ? (
                <Boton callback={() => setSidebarVisible(true)}>
                  Aceptar propuesta
                </Boton>
              ) : null
            }
          >
            {propuesta.nombre
              ? `Propuesta sábana - ${propuesta.nombre}`
              : "Propuesta comercial sábana"}
          </Titulo>

          {datosAceptacion?.fechaAceptacion && (
            <Card
              css={{
                marginBottom: "1.5em",
                backgroundColor: "var(--green-50)",
                border: "1px solid var(--green-200)",
              }}
            >
              <div
                css={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
              >
                <i
                  className="pi pi-check-circle"
                  css={{ fontSize: "1.5rem", color: "var(--green-600)" }}
                />
                <div>
                  <div css={{ fontWeight: "bold", color: "var(--green-700)" }}>
                    Propuesta aceptada
                  </div>
                  <div css={{ color: "var(--green-600)" }}>
                    {datosAceptacion.nombreAceptador
                      ? `Aceptada por ${datosAceptacion.nombreAceptador} el `
                      : "Aceptada el "}
                    {fechaCorta(datosAceptacion.fechaAceptacion)}
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card css={{ marginBottom: "1.5em" }}>
            <Titulo>Importante, leer con atención</Titulo>
            <TextoConFormato texto={propuesta.textoPropuestaImportante} />
          </Card>

          <Card css={{ marginBottom: "1.5em" }}>
            <Titulo>Detalles</Titulo>
            <div className="grid">
              <CampoInformacion nombreCampo="Cliente">
                {propuesta.cliente?.razonsocial}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Nombre">
                {propuesta.nombre || "-"}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Fecha de emisión">
                {fechaCorta(propuesta.fechaEmision)}
              </CampoInformacion>
              <CampoInformacion nombreCampo="Precios">
                {propuesta.masiva ? "Más IVA" : "IVA incluido"}
              </CampoInformacion>
              {propuesta.notas && (
                <CampoInformacion nombreCampo="Notas">
                  {propuesta.notas.split("\n").map((parrafo, index) => (
                    <span key={index}>
                      {parrafo}
                      <br />
                    </span>
                  ))}
                </CampoInformacion>
              )}
            </div>
          </Card>

          {agrupar(propuesta.propuestaSabanaCertificadoTipos).length === 0 ? (
            <Card css={{ marginBottom: "1.5em" }}>
              <div>No hay items en la propuesta</div>
            </Card>
          ) : (
            agrupar(propuesta.propuestaSabanaCertificadoTipos).map((grupo) => (
              <Card
                key={grupo.certificadoTipoId}
                css={{ marginBottom: "1.5em" }}
              >
                <Titulo>{grupo.certificadoTipoNombre || "Actividades"}</Titulo>
                <TablaFiltrable conFiltros={false} value={grupo.items} size="small" dataKey="id">
                  <Column
                    header="Actividad"
                    body={(item) => item.actividadTipo?.nombre || "-"}
                  />
                  <Column header="Precio" body={precioBody} />
                  <Column header="Facturación" body={frecuenciaBody} />
                </TablaFiltrable>
              </Card>
            ))
          )}

          {Array.isArray(propuesta.adicionales) &&
            propuesta.adicionales.length > 0 && (
              <Card css={{ marginBottom: "1.5em" }}>
                <Titulo>Adicionales</Titulo>
                {propuesta.adicionales.map((a, index) => (
                  <div
                    key={index}
                    css={{
                      padding: "0.75rem 0",
                      borderBottom:
                        index < propuesta.adicionales.length - 1
                          ? "1px solid var(--surface-200)"
                          : "none",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <div css={{ flex: 1, minWidth: "200px" }}>
                      <strong>{a.descripcion}</strong>
                    </div>
                    <div
                      css={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <span css={{ fontWeight: "bold" }}>{a.precio}</span>
                      <span css={{ color: "var(--grisTextos)" }}>
                        {a.moneda?.simbolo || a.moneda?.nombre || ""}
                      </span>
                    </div>
                  </div>
                ))}
              </Card>
            )}

          <Card css={{ marginBottom: "1.5em" }}>
            <Titulo>Aceptación</Titulo>
            <TextoConFormato texto={propuesta.textoPropuestaAceptacion} />
          </Card>

          {!datosAceptacion?.fechaAceptacion && (
            <div
              css={{
                textAlign: "center",
                marginTop: "2em",
                marginBottom: "2em",
              }}
            >
              <Boton callback={() => setSidebarVisible(true)}>
                Aceptar propuesta
              </Boton>
            </div>
          )}

          <Sidebar
            visible={sidebarVisible}
            onHide={() => setSidebarVisible(false)}
            position="right"
            className="w-full lg:w-30rem md:w-30rem"
          >
            <Titulo>Aceptar propuesta sábana</Titulo>
            <Formulario
              config={formularioAceptacionConfig}
              loader={loaderAceptacion}
            />
          </Sidebar>
        </div>
      )}
    </main>
  );
}
